import qs from 'qs';

import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import { request } from '../common';

import { BASE_PATH } from './constants';

// ============================================
// Rate cards
// ============================================
// The current published `contract-svc-lux-loyalty` package does not expose the CPP rate-card list
// endpoints, so these types are modeled directly from the fields used by the admin UI and the
// request/response wrappers below. Keep them narrow and explicit so they stay honest to the code.

export type CppPartnerType = string;
export type CppPricingModel = 'FLAT' | 'TIERED';
export type CppRateCardStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

/** One volume band. cpp is in cents; bounds inclusive on both ends. */
export interface CppRateTier {
  minPoints: number;
  maxPoints: number | null;
  cpp: number;
}

/** A rate card as used throughout the CPP UI. */
export interface CppRateCard {
  id: string;
  partnerId: string;
  reference: string;
  pricingModel: CppPricingModel;
  cppMinCents: number;
  cppMaxCents: number;
  currency?: string;
  effectiveStart: string;
  effectiveEnd: string | null;
  status: CppRateCardStatus;
  tiers: Array<CppRateTier>;
  createdAt?: string;
  updatedAt?: string;
}

/** Filters for the list endpoint. */
export type CppRateCardListFilters =
  paths['/api/lux-loyalty/partners/v1/cost-per-point/rate-cards']['get']['parameters']['query'];

/** Body for creating a rate card. */
export interface CreateCppRateCardBody {
  partnerId: string;
  pricingModel: CppPricingModel;
  reference?: string;
  cppMinCents?: number;
  cppMaxCents?: number;
  currency?: string;
  effectiveStart: string;
  effectiveEnd?: string | null;
  tiers: Array<CppRateTier>;
}

/** Body for editing a rate card (all optional; server enforces state rules). */
export type UpdateCppRateCardBody = Partial<CreateCppRateCardBody>;

/** Response of the expiring-soon endpoint. */
export interface CppExpiringResponse {
  count: number;
  cards: Array<CppRateCard>;
}

// ============================================
// Partners
// ============================================

/**
 * A partner as returned by the partner directory endpoint.
 *
 * Derived from the generated contract rather than hand-modelled: the endpoint now carries the
 * billing fields the invoice needs (`legalName`, `address`, `gstPercentage`), and restating them
 * here would let the admin UI drift from the service.
 */
export type CppPartner =
  paths['/api/lux-loyalty/partners/v1/partners']['get']['responses'][200]['content']['application/json']['result'][number];

/** Path/body types for `PATCH /partners/v1/partners/{partnerId}`. */
type UpdatePartnerPath = paths['/api/lux-loyalty/partners/v1/partners/{partnerId}']['patch'];

/**
 * Body for updating a partner's billing details.
 *
 * The service accepts only these three fields and requires at least one — everything else on a
 * partner (limits, permission flags, webhook) is managed outside this endpoint.
 */
export type UpdateCppPartnerBody = UpdatePartnerPath['requestBody']['content']['application/json'];

/** 200 body of `PATCH /partners/v1/partners/{partnerId}`. */
export type UpdateCppPartnerResponse = UpdatePartnerPath['responses'][200]['content']['application/json'];

/** Path/body types for `POST /partners/v1/partners`. */
type CreatePartnerPath = paths['/api/lux-loyalty/partners/v1/partners']['post'];

/**
 * Body for creating a partner. `name`, `apiKeyId` and `partnerType` are required; every other
 * field is optional and falls back to the service default when omitted. `status` is not accepted —
 * a new partner always starts ACTIVE.
 */
export type CreatePartnerBody = CreatePartnerPath['requestBody']['content']['application/json'];

/** 201 body of `POST /partners/v1/partners`. */
export type CreatePartnerResponse = CreatePartnerPath['responses'][201]['content']['application/json'];

// ============================================
// Partner invoicing
// ============================================
// Every type here is derived from the generated contract paths, so the admin UI cannot drift from
// the service. Nothing in this section is hand-modelled.

type InvoicePath = paths['/api/lux-loyalty/partners/v1/cost-per-point/invoice']['post'];
type InvoiceTransactionsPath = paths['/api/lux-loyalty/partners/v1/cost-per-point/invoice/transactions']['get'];

/**
 * Query for `POST /cost-per-point/invoice`. Dates are plain `YYYY-MM-DD` Sydney days.
 *
 * The endpoint is a POST because computing an invoice persists the per-transaction costs it
 * works out; `mode` is required so nothing writes by accident.
 */
export type CppInvoiceQuery = InvoicePath['parameters']['query'];

/**
 * How the invoice is computed.
 *
 * Selects whether stored per-transaction costs may be READ, not how they are written. `preview` —
 * what viewing an invoice does — is served straight from storage when the period is fully and
 * coherently cached, and recomputes otherwise. `refresh` always recomputes and never reads storage,
 * which is what is needed when underlying data changed: a backdated transaction landing in a tiered
 * period shifts every later row into a different band.
 *
 * Whenever either mode computes it persists the WHOLE period; there is no partial write, because a
 * day's stored costs are only summable while they all come from one costing of that day. Neither
 * mode writes anything while the period has blockers.
 */
export type CppInvoiceMode = CppInvoiceQuery['mode'];

/** 200 body of `POST /cost-per-point/invoice`. */
export type CppInvoiceResponse = InvoicePath['responses'][200]['content']['application/json'];

/** The invoice itself: totals, per-day rows, and the invoiceability gate. */
export type CppInvoice = CppInvoiceResponse['result'];

/** One day of the invoice, priced at that day's rate card. */
export type CppInvoiceDay = CppInvoice['days'][number];

/**
 * A structured blocker or warning. `code` is a literal union rather than a string precisely so
 * the UI can branch on it — see `invoiceNotes.ts` for the copy each code maps to.
 */
export type CppInvoiceNote = CppInvoice['blockers'][number];

/** A blocker/warning code, e.g. `PERIOD_NOT_SETTLED`. */
export type CppInvoiceNoteCode = CppInvoiceNote['code'];

/** Whole-period figures. Shared verbatim by the invoice and the transaction views. */
export type CppInvoiceTotals = CppInvoice['totals'];

/**
 * One priced volume band. Per-band amounts are individually rounded and are documented by the
 * service as NOT summable — the authoritative period figure is always `CppInvoiceTotals.amountCents`.
 */
export type CppInvoiceBand = CppInvoiceDay['tierBreakdown'][number];

/** A refund landing on a day, priced against the day it reverses rather than the day it lands. */
export type CppInvoiceRefundLine = CppInvoiceDay['refunds'][number];

/**
 * Query for `GET /cost-per-point/invoice/transactions`. The service defaults `offset`, `limit` and
 * `sort_order`, so they are optional here even though the generated contract marks them required.
 */
export type CppInvoiceTransactionsQuery = Pick<
  InvoiceTransactionsPath['parameters']['query'],
  'partnerId' | 'start' | 'end'
> &
  Partial<Omit<InvoiceTransactionsPath['parameters']['query'], 'partnerId' | 'start' | 'end'>>;

/** 200 body of `GET /cost-per-point/invoice/transactions` (one page plus whole-period siblings). */
export type CppInvoiceTransactionsResponse = InvoiceTransactionsPath['responses'][200]['content']['application/json'];

/** One ledger row with the charge attributed to it. */
export type CppInvoiceTransaction = CppInvoiceTransactionsResponse['result'][number];

/** The transaction type filter accepted by the transactions endpoint. */
export type CppInvoiceTransactionType = CppInvoiceTransaction['transactionType'];

/** House paginated envelope returned alongside a page of transactions. */
export type CppInvoicePagination = CppInvoiceTransactionsResponse['pagination'];

type RequestOptions = {
  signal?: AbortSignal;
};

const CPP_BASE = `${BASE_PATH}/partners/v1/cost-per-point`;

/** Lists CPP rate cards with optional partner/status filters. */
export async function listCppRateCards(
  filters: CppRateCardListFilters = {},
  options: RequestOptions = {},
): Promise<{ result: Array<CppRateCard> }> {
  const query = qs.stringify(filters, { skipNulls: true });
  return request(`${CPP_BASE}/rate-cards${query ? `?${query}` : ''}`, { ...options, method: 'GET' });
}

/** Fetches a single CPP rate card by id. */
export async function getCppRateCard(id: string, options: RequestOptions = {}): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}`, { ...options, method: 'GET' });
}

/** Creates a CPP rate card (as DRAFT). */
export async function createCppRateCard(body: CreateCppRateCardBody): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Updates a CPP rate card (state-aware on the server). */
export async function updateCppRateCard(id: string, body: UpdateCppRateCardBody): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Activates a DRAFT rate card. */
export async function activateCppRateCard(id: string): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}/activate`, { method: 'POST' });
}

/** Deactivates an ACTIVE rate card (started -> INACTIVE, future -> DRAFT). */
export async function deactivateCppRateCard(id: string): Promise<{ result: CppRateCard }> {
  return request(`${CPP_BASE}/rate-cards/${id}/deactivate`, { method: 'POST' });
}

/** Deletes a DRAFT rate card. */
export async function deleteCppRateCard(id: string): Promise<{ result: { id: string } }> {
  return request(`${CPP_BASE}/rate-cards/${id}`, { method: 'DELETE' });
}

/** Lists ACTIVE rate cards expiring within `withinDays` (default 30). */
export async function listExpiringCppRateCards(
  withinDays = 30,
  options: RequestOptions = {},
): Promise<{ result: CppExpiringResponse }> {
  return request(`${CPP_BASE}/expiring-soon?withinDays=${withinDays}`, { ...options, method: 'GET' });
}

/** Lists partners, for partner dropdowns/filters. */
export async function listCppPartners(options: RequestOptions = {}): Promise<{ result: Array<CppPartner> }> {
  return request(`${BASE_PATH}/partners/v1/partners`, { ...options, method: 'GET' });
}

/**
 * Creates a partner. Loyalty-admin only.
 *
 * `apiKeyId` must be unique across partners — the service answers 409 for a duplicate.
 */
export async function createCppPartner(body: CreatePartnerBody): Promise<CreatePartnerResponse> {
  return request(`${BASE_PATH}/partners/v1/partners`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Computes the day-aggregated CPP invoice for a partner over a date range.
 *
 * A POST rather than a GET because computing an invoice persists the per-transaction costs it
 * works out, so it is not a safe method. `mode` decides whether stored costs may be read and is
 * required — see {@link CppInvoiceMode}. Parameters go on the query string, not in a body; the
 * endpoint takes no body.
 *
 * Feeds the invoice document: totals, per-day priced rows and the `invoiceable` gate. The
 * per-transaction ledger is deliberately not part of this payload — use
 * {@link getCppInvoiceTransactions} for that.
 */
export async function generateCppInvoice(
  query: CppInvoiceQuery,
  options: RequestOptions = {},
): Promise<CppInvoiceResponse> {
  return request(`${CPP_BASE}/invoice?${qs.stringify(query, { skipNulls: true })}`, { ...options, method: 'POST' });
}

/**
 * Fetches one page of per-transaction CPP invoice detail for a partner over a date range.
 *
 * Every page carries the same whole-period `totals`, `blockers` and `invoiceable` flag, since a
 * row's cost depends on the other rows landing on its day and cannot be computed per page.
 */
export async function getCppInvoiceTransactions(
  query: CppInvoiceTransactionsQuery,
  options: RequestOptions = {},
): Promise<CppInvoiceTransactionsResponse> {
  return request(`${CPP_BASE}/invoice/transactions?${qs.stringify(query, { skipNulls: true })}`, {
    ...options,
    method: 'GET',
  });
}

/**
 * Updates a partner's billing details (GST rate, legal entity name, address).
 *
 * Loyalty-admin only, and the only partner fields this endpoint accepts — the service requires at
 * least one of them to be present.
 */
export async function updateCppPartner(
  partnerId: string,
  body: UpdateCppPartnerBody,
): Promise<UpdateCppPartnerResponse> {
  return request(`${BASE_PATH}/partners/v1/partners/${partnerId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}
